/**
 * Meeting Service — /agenda/meetings (P12 §5 "Meetings")
 *
 * - respond: ACCEPTED|DECLINED|TENTATIVE (+ razón si declineReasonRequired;
 *   MANDATORY rechaza DECLINED → usar exception-request).
 * - exception-decision/override son acciones del organizador/admin sobre un
 *   participante concreto.
 */

import { apiClient } from '@/services/api-client.ts'
import { unwrap } from './http'
import type {
  AgendaItem,
  ConflictResolution,
  CreateMeetingInput,
  MeetingItem,
  MeetingRespondInput,
  MeetingResponsesSummary,
  UpdateMeetingInput
} from '@/types/agenda'

export const meetingService = {
  /** POST /agenda/meetings — COORD; participantes dentro del alcance */
  create(dto: CreateMeetingInput): Promise<MeetingItem> {
    return unwrap<MeetingItem>(apiClient.post('/agenda/meetings', dto))
  },

  /** GET /agenda/meetings/:id */
  get(id: number): Promise<MeetingItem> {
    return unwrap<MeetingItem>(apiClient.get(`/agenda/meetings/${id}`))
  },

  /** PATCH /agenda/meetings/:id — reschedule resetea respuestas no-DECLINED */
  update(id: number, dto: UpdateMeetingInput): Promise<MeetingItem> {
    return unwrap<MeetingItem>(apiClient.patch(`/agenda/meetings/${id}`, dto))
  },

  /** POST /agenda/meetings/:id/cancel */
  cancel(id: number, reason?: string): Promise<AgendaItem> {
    return unwrap<AgendaItem>(apiClient.post(`/agenda/meetings/${id}/cancel`, { reason }))
  },

  /** POST /agenda/meetings/:id/respond — con resolución de conflictos opcional */
  respond(
    id: number,
    payload: MeetingRespondInput & { conflictResolution?: ConflictResolution }
  ): Promise<MeetingItem> {
    return unwrap<MeetingItem>(apiClient.post(`/agenda/meetings/${id}/respond`, payload))
  },

  /** POST /agenda/meetings/:id/exception-request — participante solicita excepción */
  requestException(
    id: number,
    payload: { justification: string; proposedAlternative?: string }
  ): Promise<MeetingItem> {
    return unwrap<MeetingItem>(
      apiClient.post(`/agenda/meetings/${id}/exception-request`, payload)
    )
  },

  /** POST /agenda/meetings/:id/participants/:userId/exception-decision — organizador */
  decideException(
    id: number,
    userId: number,
    payload: { decision: 'APPROVED' | 'REJECTED'; reason: string }
  ): Promise<MeetingItem> {
    return unwrap<MeetingItem>(
      apiClient.post(
        `/agenda/meetings/${id}/participants/${userId}/exception-decision`,
        payload
      )
    )
  },

  /** POST /agenda/meetings/:id/participants/:userId/override — admin, razón obligatoria */
  overrideParticipant(
    id: number,
    userId: number,
    payload: { reason: string }
  ): Promise<MeetingItem> {
    return unwrap<MeetingItem>(
      apiClient.post(`/agenda/meetings/${id}/participants/${userId}/override`, payload)
    )
  },

  /** GET /agenda/meetings/:id/responses — dashboard del organizador */
  getResponses(id: number): Promise<MeetingResponsesSummary> {
    return unwrap<MeetingResponsesSummary>(apiClient.get(`/agenda/meetings/${id}/responses`))
  }
}

export default meetingService
